// Storage keys holding identity, credentials, or anything else scoped to the
// signed-in user. Everything listed here is wiped on logout via clearSession().
// Keep this the single source of truth for "what we persist about the signed-in
// user" — import these constants at the write sites rather than repeating the
// literals, so the two lists cannot drift apart.
export const STORAGE_KEYS = {
  USER_INFO: "userInfo",
  MEDIA_LIBRARY_TOKEN: "mediaLibraryUploaderToken",
  VIEWED_PORTAL_NOTIFICATIONS: "viewedPortalNotifications",
  REFERRAL_CODE: "referral_code",
}

// The last-visited account is stored per user id, so it has no fixed key and is
// cleared by prefix instead.
const LAST_VISITED_ACCOUNT_PREFIX = "lastVisitedAccountKey_"

export const lastVisitedAccountKey = (userId) => `${LAST_VISITED_ACCOUNT_PREFIX}${userId}`

const teardownHandlers = []

// Register work that must happen whenever a signed-in session ends: resetting
// the chat widgets, dropping the analytics identity, emptying the store.
//
// It hangs off clearSession() rather than off the logout action because that
// action is dispatched from only two places — the Sign out menu and the delete
// account button — while sessions also end when boot finds the user
// unauthenticated, when the boot error screen signs out, and when a refresh
// token expires in an idle tab, which is the ordinary overnight case and by far
// the most common of the lot. clearSession() is the one call every one of those
// paths already makes, so registering here is what makes coverage automatic.
//
// This module deliberately imports nothing: src/auth/keycloak.js calls
// clearSession(), and reaching the store or the services from here would close
// the loop keycloak -> store -> modules -> security/actions -> keycloak.
export const onSessionEnd = (handler) => {
  teardownHandlers.push(handler)
}

// Remove all identity / user-scoped data from storage, then run the registered
// teardown. Idempotent — safe to call on every path that ends a session.
export const clearSession = () => {
  Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key))

  // Object.keys snapshots first, so removing while iterating is safe.
  Object.keys(localStorage)
    .filter((key) => key.startsWith(LAST_VISITED_ACCOUNT_PREFIX))
    .forEach((key) => localStorage.removeItem(key))

  // Everything this app keeps in sessionStorage belongs to one signed-in session
  // — experiment arm, nudge variant, the paid-plan claim guard, the chunk-reload
  // guard — so none of it should carry into the next user on a shared machine.
  sessionStorage.clear()

  teardownHandlers.forEach((handler) => {
    // One failing handler must not stop the others, and must never prevent the
    // logout redirect that usually follows.
    try {
      handler()
    } catch (e) {
      console.error("[AUTH] Session teardown handler failed", e)
    }
  })
}
